import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { EncryptionService } from '../services/encryption.service';

function isLoginRequest(request: HttpRequest<unknown>): boolean {
  return request.url.includes('/auth/login');
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const encryptionService = inject(EncryptionService);

  /** Attach the auth token + encryption key headers to the outgoing request. */
  const attachHeaders = (
    request: HttpRequest<unknown>
  ): HttpRequest<unknown> => {
    const token = authService.getToken();

    const headers: { [key: string]: string } = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Login must never send the encryption key — a stale key blocks
    // authentication after the backend encryption key is rotated.
    if (!isLoginRequest(request)) {
      const encryptionKey = encryptionService.getStoredEncryptionKey();
      if (encryptionKey) {
        headers['x-encrypted-key'] = encryptionKey;
      }
    }

    return Object.keys(headers).length > 0
      ? request.clone({ setHeaders: headers })
      : request;
  };

  // Login does not need the encryption key, so skip waiting for IndexedDB.
  if (isLoginRequest(req) || encryptionService.isReady()) {
    return next(attachHeaders(req));
  }

  // On a hard refresh the encryption key is read from IndexedDB asynchronously.
  // Defer the request until that read completes so the `x-encrypted-key` header
  // is always present — otherwise the backend receives an unkeyed request and
  // returns empty/zeroed data.
  return toObservable(encryptionService.isReady).pipe(
    filter(ready => ready),
    take(1),
    switchMap(() => next(attachHeaders(req)))
  );
};
